import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { Icon } from "@/components/ui/Icon";
import { Brand } from "./Brand";
import { FOOTER_EXPLORE, FOOTER_SERVICES, SOCIALS } from "./nav";
import { CONTACT } from "@/lib/constants";
import { useSubscribeMutation } from "@/hooks";
import { toast } from "@/store/toasts";

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function Footer() {
  const [email, setEmail] = useState("");
  const subscribe = useSubscribeMutation();

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!EMAIL.test(email.trim())) {
      toast.error("Check your email", "That address does not look right.");
      return;
    }
    subscribe.mutate(email.trim(), {
      onSuccess: () => {
        toast.success("You are subscribed", "Our journal arrives once a month.");
        setEmail("");
      },
      onError: () => toast.error("Could not subscribe", "Please try again in a moment."),
    });
  };

  return (
    <footer className="site-footer">
      <div className="container container--wide">
        <div className="footer__top">
          <div className="footer__brand">
            <Brand />
            <p className="footer__blurb">
              Civil engineering precision and architectural creativity, delivered end to end — from
              feasibility studies to the keys in your hand.
            </p>
            <div className="footer__socials">
              {SOCIALS.map((social) => (
                <a key={social.label} href={social.href} aria-label={social.label} rel="noopener">
                  <Icon name={social.icon} />
                </a>
              ))}
            </div>
          </div>

          <div className="footer__col">
            <h2>Explore</h2>
            <ul>
              {FOOTER_EXPLORE.map((link) => (
                <li key={link.to}>
                  <Link to={link.to}>{link.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="footer__col">
            <h2>Services</h2>
            <ul>
              {FOOTER_SERVICES.map((link) => (
                <li key={link.to}>
                  <Link to={link.to}>{link.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="footer__col">
            <h2>Get in touch</h2>
            <div className="footer__contact-item">
              <Icon name="map-pin" />
              <span>
                4th Floor, Sector 44 Business Centre,
                <br />
                Gurugram, Haryana 122003
              </span>
            </div>
            <div className="footer__contact-item">
              <Icon name="phone" />
              <a href={CONTACT.phoneHref}>{CONTACT.phone}</a>
            </div>
            <div className="footer__contact-item">
              <Icon name="mail" />
              <a href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a>
            </div>

            <form className="footer__newsletter" onSubmit={onSubmit} noValidate>
              <label className="sr-only" htmlFor="footerEmail">
                Email address for the newsletter
              </label>
              <input
                id="footerEmail"
                type="email"
                name="email"
                placeholder="Email for our journal"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
              <button type="submit" disabled={subscribe.isPending}>
                {subscribe.isPending ? "…" : "Join"}
              </button>
            </form>
          </div>
        </div>

        <div className="footer__bottom">
          <span>© {new Date().getFullYear()} Indraprastha Engineers &amp; Architects. Demonstration prototype.</span>
          <div className="footer__legal">
            <Link to="/privacy">Privacy Policy</Link>
            <Link to="/terms">Terms of Use</Link>
            <Link to="/search">Search</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
